import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import styles from './FuelTransactionInfo.styles';
import ListItem from '../shared/ListItem';
import ListEditItem from '../shared/ListEditItem';
import FuelTransactionType from '../shared/FuelTransactionType';
import FuelTransactionFlagged from '../shared/FuelTransactionFlagged';
import FuelTransactionFlagReasons from '../shared/FuelTransactionFlagReasons';
import LinkToVehicle from '../shared/LinkToVehicle';
import LinkToTrip from '../shared/LinkToTrip';
import LinkToUser from '../shared/LinkToUser';
import LoadingIcon from '../shared/LoadingIcon';
import { renderText } from '../shared/DrawerLayout/renderText';
import { renderDateTime } from '../shared/TableLayout';
import { selectSelectedFuelTransaction } from '../../redux/store/userData/fuelTransactions';
import { selectTripLoading } from '../../redux/store/userData/trips/selectors';
import { selectUserLoading } from '../../redux/store/userData/users';
import { formatFuelLevel, formatLitres } from '../../utils';

const FuelTransactionInfo: React.FC = () => {
  const intl = useIntl();
  const fuelTransaction = useSelector(selectSelectedFuelTransaction);
  const isTripLoading = useSelector(selectTripLoading);
  const isUserLoading = useSelector(selectUserLoading);

  const label = (key: string, defaultMessage: string) =>
    intl.formatMessage({
      id: `fleetadmin.fuelTransactions.table.${key}`,
      defaultMessage,
    });

  return (
    <>
      <div className="fuel-transaction-info">
        <ListItem
          testid="uniqueId"
          label={label('uniqueid', 'No (transaction)')}
          value={renderText(fuelTransaction?.uniqueId)}
        />
        <ListItem
          testid="type"
          label={label('type', 'Type')}
          value={<FuelTransactionType type={fuelTransaction?.type} />}
        />
        {fuelTransaction?.flagged && (
          <>
            <ListItem
              testid="flagged"
              label={label('flagged', 'Flagged')}
              value={<FuelTransactionFlagged flagged />}
            />
            <ListItem
              testid="flags"
              label={label('flags', 'Reason')}
              value={
                <FuelTransactionFlagReasons flags={fuelTransaction.flags} />
              }
            />
          </>
        )}
        <ListItem
          testid="timestamp"
          label={label('timestamp', 'Date')}
          value={renderDateTime(fuelTransaction?.timestamp)}
        />
        <ListItem
          testid="fuelStationBrandName"
          label={label('fuelstationbrandname', 'Station')}
          value={renderText(fuelTransaction?.fuelStationBrandName)}
        />
        <ListItem
          testid="fuelStationAddress"
          label={label('fuelstationaddress', 'Address')}
          value={renderText(fuelTransaction?.fuelStationAddress)}
        />
        {fuelTransaction?.plate ? (
          <ListEditItem
            testid="plate"
            label={label('plate', 'Plate')}
            leftItem={
              <LinkToVehicle
                carId={fuelTransaction?.carId}
                text={fuelTransaction?.plate}
              />
            }
          />
        ) : (
          <ListItem label={label('plate', 'Plate')} value="-" />
        )}
        {fuelTransaction?.code ? (
          <ListEditItem
            testid="code"
            label={label('code', 'Code (trip)')}
            leftItem={
              <LinkToTrip
                testid="tripLink"
                tripCode={fuelTransaction?.code}
                tripId={fuelTransaction?.tripId}
                service={null}
              />
            }
            rightItem={isTripLoading ? <LoadingIcon /> : null}
          />
        ) : (
          <ListItem label={label('code', 'Code (trip)')} value="-" />
        )}
        {fuelTransaction?.userReferralCode ? (
          <ListEditItem
            testid="userReferralCode"
            label={label('userreferralcode', 'Code (user)')}
            leftItem={
              <LinkToUser
                userId={fuelTransaction?.userId}
                userCode={fuelTransaction?.userReferralCode}
              />
            }
            rightItem={isUserLoading ? <LoadingIcon /> : null}
          />
        ) : (
          <ListItem
            label={label('userreferralcode', 'Code (user)')}
            value="-"
          />
        )}
        <ListItem
          testid="fleetName"
          label={label('fleetname', 'Fleet')}
          value={renderText(fuelTransaction?.fleetName)}
        />
        <ListItem
          testid="brandName"
          label={label('brandid', 'Brand')}
          value={renderText(fuelTransaction?.brandName)}
        />
        <ListItem
          testid="merchantName"
          label={label('merchantname', 'Merchant')}
          value={renderText(fuelTransaction?.merchantName)}
        />
        <ListItem
          testid="fuelLevel"
          label={label('fuellevel', 'Fuel level')}
          value={formatFuelLevel(
            fuelTransaction?.fuelLevelBefore,
            fuelTransaction?.fuelLevelAfter
          )}
        />
        <ListItem
          testid="quantity"
          label={label('quantity', 'Quantity')}
          value={formatLitres(fuelTransaction?.quantity)}
        />
        <ListItem
          testid="pumpNumber"
          label={label('pumpnumber', 'Pump')}
          value={renderText(fuelTransaction?.pumpNumber)}
        />
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default FuelTransactionInfo;
